import type { ClaimTariffStatusValue } from '@billing/validation/tariff';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { useDisplayErrorNotification } from 'hooks/useDisplayErrorNotification';
import { MRT_ColumnFiltersState } from 'mantine-react-table';

import { NoDataFoundError } from '../APIRequestError';
import { handleSentryErrors } from '../HandleSentryErrors';
import { useCurrentUserStore } from 'store/UserStore';

export interface StatusesCountResult {
  [key: string]: number;
}

export interface CountParams {
  siteID?: string | string[];
  providerID?: string;
  userID?: string;
  tariffStatus?: ClaimTariffStatusValue;
  filters?: MRT_ColumnFiltersState;
  globalFilter?: string;
}

interface UseCountQueryParams {
  queryKey: any[];
  functionName: string;
  enabled: boolean;
  fetchFunction: (params: CountParams) => Promise<StatusesCountResult | null>;
  params: CountParams;
}

export type CountReturnTypes = StatusesCountResult | number;

/**
 * Custom hook for fetching count data
 *
 * @param queryKey - The key for the query.
 * @param fetchFunction - The function to fetch the data.
 * @param params - The parameters for the fetch function.
 * @param functionName - The name of the function.
 * @param enabled - Flag to enable/disable the query.
 *
 * @returns The result of the count query along with error handling.
 */

export const useCountQuery = ({
  queryKey,
  fetchFunction,
  params,
  functionName,
  enabled,
}: UseCountQueryParams): UseQueryResult<CountReturnTypes, Error> => {
  const { handleNoDataFound } = useDisplayErrorNotification();
  const user = useCurrentUserStore();

  return useQuery<CountReturnTypes, Error>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: queryKey,
    queryFn: async () => {
      const data = await fetchFunction(params);

      if (!data && typeof data !== 'number') {
        const error = new NoDataFoundError(
          `No data found for ${functionName}`,
          functionName,
          params
        );
        handleSentryErrors({
          error,
          functionName,
          params,
          displayNotification: false,
          handleNoDataFound,
          user,
        });
      }
      return data;
    },
    meta: {
      functionName: functionName,
    },
    enabled: enabled,
    staleTime: 60000,
    retry: false,
  });
};
